import { Handshake, Plus } from "lucide-react";
import { useTranslate } from "ra-core";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { parseAmount } from "../cardModel";
import { useFormatAmount } from "../useFormatAmount";
import { DeleteIconButton } from "./DeleteIconButton";
import { SectionTitle } from "./SectionTitle";

export interface DealItem {
  key: string | number;
  name: string;
  amount: number | null;
  description?: string | null;
}

export interface NewDeal {
  name: string;
  amount: number | null;
  description: string;
}

export interface DealsSectionProps {
  items: DealItem[];
  onAdd: (deal: NewDeal) => Promise<void> | void;
  onDelete: (key: DealItem["key"]) => Promise<void> | void;
}

const NewDealForm = ({
  onSubmit,
  onCancel,
}: {
  onSubmit: (deal: NewDeal) => Promise<void> | void;
  onCancel: () => void;
}) => {
  const translate = useTranslate();
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!name.trim()) return;
    await onSubmit({
      name: name.trim(),
      amount: parseAmount(amount),
      description: description.trim(),
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-2 rounded-md border p-3"
    >
      <Input
        autoFocus
        required
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder={translate("crm.operations.deals.name")}
        aria-label={translate("crm.operations.deals.name")}
      />
      <Input
        inputMode="decimal"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        placeholder={translate("crm.operations.deals.amount")}
        aria-label={translate("crm.operations.deals.amount")}
      />
      <Textarea
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder={translate("crm.operations.deals.description")}
        aria-label={translate("crm.operations.deals.description")}
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {translate("ra.action.cancel")}
        </Button>
        <Button type="submit" size="sm" disabled={!name.trim()}>
          {translate("ra.action.add")}
        </Button>
      </div>
    </form>
  );
};

export const DealsSection = ({ items, onAdd, onDelete }: DealsSectionProps) => {
  const translate = useTranslate();
  const formatAmount = useFormatAmount();
  const [isAdding, setIsAdding] = useState(false);
  const total = items.reduce((sum, item) => sum + (item.amount ?? 0), 0);

  return (
    <section className="flex flex-col gap-2">
      <SectionTitle icon={<Handshake />}>
        {translate("crm.operations.deals.title")}
        {items.length > 0 && (
          <span className="ml-auto text-xs font-normal text-muted-foreground">
            {translate("crm.operations.deals.total")}: {formatAmount(total)}
          </span>
        )}
      </SectionTitle>
      {items.map((item) => (
        <div
          key={item.key}
          className="flex items-start gap-2 rounded-md border px-3 py-2"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm font-medium">{item.name}</span>
              <span className="shrink-0 text-sm">
                {formatAmount(item.amount)}
              </span>
            </div>
            {item.description && (
              <p className="whitespace-pre-wrap text-xs text-muted-foreground">
                {item.description}
              </p>
            )}
          </div>
          <DeleteIconButton
            label={translate("crm.operations.deals.delete")}
            onConfirm={() => onDelete(item.key)}
          />
        </div>
      ))}
      {isAdding ? (
        <NewDealForm
          onCancel={() => setIsAdding(false)}
          onSubmit={async (deal) => {
            await onAdd(deal);
            setIsAdding(false);
          }}
        />
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => setIsAdding(true)}
        >
          <Plus />
          {translate("crm.operations.deals.add")}
        </Button>
      )}
    </section>
  );
};
